<script setup lang="ts">
/**
 * Long free text in a narrow spot — a grid cell, a card line — cut to a fixed
 * number of `lines`, with the whole string one hover away. The kit tooltip
 * shows the full text, but only where the clamp actually cut something: a
 * bubble repeating a fully visible value is noise on every hover.
 *
 * Whether the clamp bit is measured on the painted box (`scrollHeight >
 * clientHeight`) and kept true by one ResizeObserver while the column is
 * dragged or the window resized. A box that has not been laid out yet
 * (`clientHeight === 0`) counts as cut: reading it as „fits“ is the one
 * reading that could lose text.
 *
 * The cut is painted, never stored — copying and browser search still see the
 * full string. The host must let the box be narrow (`min-width: 0`) and the
 * row grow.
 *
 * `#content` replaces the bubble's plain text with rich content — a bold
 * headline, a few lines — the way `NxTooltip`'s slot does. Such a bubble says
 * more than the clamped text, so it opens on every hover, cut or not.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { NxTooltip, type NxTooltipProps } from '../Tooltip';

const props = withDefaults(
  defineProps<{
    /** The full text — also what the tooltip shows. */
    text: string;
    /** Visible lines before the clamp cuts. */
    lines?: number;
    side?: NxTooltipProps['side'];
    /** Hover delay before the bubble opens, in ms. */
    delayMs?: number;
  }>(),
  { lines: 2, side: 'top', delayMs: 300 }
);

defineSlots<{
  /** Rich bubble content; replaces `text` in the bubble and shows it on every hover. */
  content?: () => unknown;
}>();

const body = ref<HTMLElement | null>(null);
/** True while the clamp is hiding part of the text. */
const clamped = ref(false);

function measure() {
  const el = body.value;
  if (el == null) return;
  clamped.value = el.clientHeight === 0 || el.scrollHeight > el.clientHeight;
}

// One observer for the component's life, re-pointed whenever the verdict
// flips and the template swaps the bare span for the wrapped one.
let observer: ResizeObserver | null = null;
watch(
  body,
  (el) => {
    observer?.disconnect();
    measure();
    if (el == null || typeof ResizeObserver === 'undefined') return;
    observer ??= new ResizeObserver(() => measure());
    observer.observe(el);
  },
  { flush: 'post' }
);

// A new text or line count changes the answer without resizing anything — a
// virtualized grid reuses the node for the next row.
watch(
  () => [props.text, props.lines],
  () => measure(),
  { flush: 'post' }
);

// The web font may still be swapping in on mount; re-measure once it is ready.
let unmounted = false;
onMounted(() => {
  void document.fonts?.ready?.then(() => {
    if (!unmounted) measure();
  });
});

onBeforeUnmount(() => {
  unmounted = true;
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <!-- Slots are not reactive: the template reads `#content` on every render,
       so a reused grid cell gains and drops the rich bubble with its row. -->
  <NxTooltip
    v-if="clamped || $slots.content"
    :text="props.text"
    :side="props.side"
    :delay-ms="props.delayMs"
  >
    <span
      ref="body"
      class="nx-clamped-text"
      :style="{ '--clamped-lines': String(props.lines) }"
      data-testid="nx-clamped-text"
    >
      {{ props.text }}
    </span>
    <template v-if="$slots.content" #content>
      <slot name="content" />
    </template>
  </NxTooltip>
  <span
    v-else
    ref="body"
    class="nx-clamped-text"
    :style="{ '--clamped-lines': String(props.lines) }"
    data-testid="nx-clamped-text"
  >
    {{ props.text }}
  </span>
</template>

<style scoped>
.nx-clamped-text {
  display: -webkit-box;
  -webkit-line-clamp: var(--clamped-lines);
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* Grid cells are often `nowrap`; the clamp needs real line breaks. */
  white-space: normal;
  /* An unbreakable token (an id, a URL) breaks instead of widening the column. */
  overflow-wrap: anywhere;
  line-height: var(--nxt-line-height-normal);
}
</style>
